import { ElementService } from "@/services/user-services";
import { useDataStore, useLocaleStore } from "@/stores";
import useExportStore from "@/stores/useExportStore";
import { Dictionary } from "@/utils/Dictionary";
import clsx from "clsx";
import { format } from "date-fns";
import { saveAs } from "file-saver";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const DISCIPLINE_MAP: Dictionary<string> = {
    ARCHI: "AR",
    STRUCT: "ST",
    MEP: "MEP",
};

const ExcelExportHandler = () => {
    const activeRegion = useLocaleStore((state) => state.activeRegion);
    const activeLanguage = useLocaleStore((state) => state.activeLanguage);
    const availableRegions = useLocaleStore((state) => state.availableRegions);

    const versions = useDataStore((state) => state.versions);
    const disciplines = useDataStore((state) => state.disciplines);
    const useCases = useDataStore((state) => state.useCases);

    const exportData = useExportStore((state) => state.exportData);
    const beginExport = useExportStore((state) => state.beginExport);
    const finishExport = useExportStore((state) => state.finishExport);
    const clearExport = useExportStore((state) => state.clearExport);

    const [collapsed, setCollapsed] = useState<boolean>(false);

    const { t } = useTranslation("elements");

    const elementService = useMemo(
        () => new ElementService(activeRegion!, activeLanguage!),
        [activeRegion, activeLanguage]
    );

    useEffect(() => {
        const pendingExports = exportData.filter((item) => !item.abortController);
        if (pendingExports.length === 0) return;

        pendingExports.forEach((exportItem) => {
            // Drop use-case ids that are no longer valid for the current region or the export's selected
            // disciplines, so we don't send ids for use-cases that are hidden in the frontend (e.g. the user
            // changed the country/language or discipline selection after triggering the export).
            const availableRegionCodes = availableRegions.map((r) => r.regionCode.toLowerCase());
            const validUseCaseIds = (useCases[exportItem.activeVersionId!] ?? [])
                .filter((uc) => exportItem.activeUseCaseIds.includes(uc.id))
                .filter((uc) => {
                    if (availableRegionCodes.length === 0) return true;

                    const potentialRegionCode = uc.code.slice(-2).toLowerCase();
                    if (!availableRegionCodes.includes(potentialRegionCode)) return true;

                    return potentialRegionCode === activeRegion?.toLowerCase();
                })
                .filter(
                    (uc) =>
                        !uc.attachedDiscipline ||
                        uc.attachedDiscipline.some((d) => exportItem.activeDisciplineIds.includes(d))
                )
                .map((uc) => uc.id);

            const filenamePartDate = format(new Date(), "yyyyMMdd");
            const filenamePartDisciplines = disciplines[exportItem.activeVersionId!]
                .filter((x) => exportItem.activeDisciplineIds.includes(x.id))
                .map((x) => DISCIPLINE_MAP[x.code])
                .join("-");
            const filenamePartUseCases =
                validUseCaseIds.length > 1
                    ? "multiple use-cases"
                    : useCases[exportItem.activeVersionId!].find((x) => x.id === validUseCaseIds[0])?.name || "";

            const filenameSuffix = exportItem.kind === "structured-export" ? "DATA MANAGEMENT" : "FOR CONTRACT";
            const filename = `${filenamePartDate}_${filenamePartDisciplines}_${filenamePartUseCases}_EIR ${filenameSuffix}.xlsx`;
            const abortController = new AbortController();

            const activeVersionName =
                exportItem.activeVersionId !== "wip"
                    ? versions.find((x) => x.id === exportItem.activeVersionId)?.name
                    : "wip";
            const requestedSoftware = [exportItem.activeIfcVersionId, exportItem.activeSoftwareId].filter(
                (x) => x !== null
            );

            beginExport(exportItem.id, filename, abortController);

            const exportCall =
                exportItem.kind === "structured-export" ? elementService.exportStructured : elementService.export;

            exportCall(
                requestedSoftware,
                exportItem.activeDisciplineIds,
                validUseCaseIds,
                exportItem.activeClassificationId,
                [],
                true,
                activeVersionName,
                abortController.signal
            )
                .then((blob) => {
                    finishExport(exportItem.id, blob);
                })
                .catch(() => {
                    if (abortController.signal.aborted) return;
                    finishExport(exportItem.id);
                });
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exportData]);

    // Return a floating div that is only visible when there are pending or finished exports. This should have a slide in animation and an option to expand collapse as well

    return (
        <AnimatePresence>
            {exportData.length > 0 && (
                <motion.aside
                    key="export-list"
                    role="dialog"
                    aria-modal={false}
                    initial={{ opacity: 0, x: 48, y: 0 }}
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    exit={{ opacity: 0, x: 48, y: 0 }}
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    className="fixed bottom-0 right-30 z-1000 pointer-events-auto w-full sm:w-[500px] select-none"
                >
                    <div className="rounded-t-lg shadow-xl border border-black/5 bg-white text-slate-900  overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-zinc-200">
                            <div
                                className="font-medium text-sm leading-none truncate"
                                title={t("export.title", { ns: "elements" })}
                            >
                                {t("export.title", { ns: "elements" })}
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => setCollapsed((c) => !c)}
                                    aria-label={collapsed ? "Expand drawer" : "Collapse drawer"}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-black/5 transition cursor-pointer"
                                >
                                    {/* Chevron icon (inline SVG) */}
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={`transition-transform ${collapsed ? "rotate-180" : ""}`}
                                        aria-hidden
                                    >
                                        <path
                                            d="M6 9l6 6 6-6"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <motion.div
                            key="drawer-content"
                            initial={false}
                            animate={{ height: collapsed ? 0 : "auto", opacity: collapsed ? 0 : 1 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                        >
                            <div className="p-4 max-h-[60vh] overflow-auto text-sm italic border-b border-zinc-200 text-zinc-600">
                                {t("export.description", { ns: "elements" })}
                            </div>
                            <ul className="m-0 p-4">
                                {exportData.map((exportItem) => {
                                    const exportFailed = exportItem.done && !exportItem.blob;
                                    return (
                                        <li key={exportItem.id} className="list-none m-2">
                                            <div className="grid grid-cols-[1fr_auto] items-start gap-3">
                                                <div className="min-w-0">
                                                    <div className="mb-1">
                                                        <p className="font-medium truncate" title={exportItem.name}>
                                                            {exportItem.name}
                                                        </p>
                                                    </div>
                                                    <ProgressBar done={exportItem.done} error={exportFailed} />
                                                    {exportFailed && (
                                                        <div className="my-1 text-red-700 text-sm italic">
                                                            Export failed
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 pt-1 shrink-0">
                                                    {exportItem.blob ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                saveAs(exportItem.blob!, exportItem.name);
                                                                clearExport(exportItem.id);
                                                            }}
                                                            aria-label="Download"
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-black/5 cursor-pointer"
                                                        >
                                                            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                                                                <path
                                                                    d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => clearExport(exportItem.id)}
                                                            aria-label="Cancel"
                                                            className="inline-flex h-8 w-8 ml-auto items-center justify-center rounded-full hover:bg-black/5 cursor-pointer"
                                                        >
                                                            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                                                                <path
                                                                    d="M18 6L6 18M6 6l12 12"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </motion.div>
                    </div>
                </motion.aside>
            )}
        </AnimatePresence>
    );
};

export default ExcelExportHandler;

const ProgressBar = ({ done, error }: { done: boolean; error: boolean }) => {
    return (
        <div className="mt-2">
            <div className="relative h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                {done ? (
                    <div
                        className={clsx("h-full", error ? "bg-red-700" : "bg-primary-500")}
                        style={{ width: `${100}%` }}
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={100}
                    />
                ) : (
                    <div className="absolute inset-0">
                        <div
                            className="indeterminate-bar absolute left-[-40%] top-0 h-full w-[40%] bg-primary-500"
                            aria-hidden
                        />
                    </div>
                )}
            </div>
            <style>{`
        @keyframes indeterminate-scan { 0% { left: -40%; } 100% { left: 100%; } }
        .indeterminate-bar { animation: indeterminate-scan 1.1s infinite linear; }
      `}</style>
        </div>
    );
};
